// ItineraryList page — a list of itineraries fetched from Firestore.
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import HotelIcon from '@mui/icons-material/Hotel';
import { useApp } from "../../../context";
import PageTemplate from "../../components/PageTemplate";
import { itineraryStatus } from "../../../utils/itineraryStatus";
import "./styles.css";

// `div`, not `main` — PageTemplate already provides the page's single <main> landmark.
const Main = styled("div")(({ theme }) => ({
  flexGrow: 1,
  minHeight: "100vh",
  padding: theme.spacing(2),
  // Tokenized surface so the page is correct in dark mode (was a hardcoded #fff).
  background: theme.palette.background.default,
}));

const ItineraryCard = styled(Paper)(({ theme }) => ({
  ...theme.typography.body1,
  display: "block",
  textAlign: "left",
  textDecoration: "none",
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(["box-shadow", "border-color"]),
  "&:hover": { boxShadow: theme.shadows[4], borderColor: theme.palette.primary.main },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const formatItineraryDates = (dates) => {
  const getMs = (d) => {
    if (!d) return null;
    if (d.seconds !== undefined) return d.seconds * 1000;
    if (typeof d.toDate === "function") return d.toDate().getTime();
    return null;
  };
  if (dates && dates.length > 1) {
    const startMs = getMs(dates?.[0]);
    const endMs = getMs(dates?.[1] || dates?.[0]);
    const startStr = startMs ? moment(startMs).format("DD MMM YYYY") : "";
    const endStr = endMs ? moment(endMs).format("DD MMM YYYY") : "";
    return `${startStr} - ${endStr}`;
  }
  const startMs = getMs(dates);
  return startMs ? moment(startMs).format("DD MMM YYYY") : "";
};

export default function ItineraryListPage() {
  const { itineraryList, getItineraryData, user } = useApp();

  useEffect(() => {
    if (user) {
      getItineraryData();
    }
    // `getItineraryData` comes off the app context unmemoised, so it is a new
    // function every render, and it sets state. Listing it here is an infinite
    // render loop, not a correctness fix. Same pattern as RecommendationQuiz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const hasItineraries = itineraryList && itineraryList.length > 0;

  return (
    <PageTemplate>
      <Main>
        <Toolbar sx={{ marginBottom: "1em" }} />
        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          <Typography component="h1" className="txt--gray" sx={{ mb: 2 }}>
            Your Itineraries
          </Typography>

          {hasItineraries ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {itineraryList.map((it) => (
                <ItineraryCard key={it.id} component={Link} to={`/itinerary/${it.id}`} elevation={1}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" component="h2" color="text.primary" sx={{ display: "flex", alignItems: "center" }}>
                        <LocationOnRoundedIcon sx={{ fontSize: "1.1rem", mr: 0.5, color: "text.secondary", flexShrink: 0 }} />
                        {it.location}
                      </Typography>
                      {it.bookings?.accommodation && <Typography variant="h6" component="h2" color="text.primary" sx={{ display: "flex", alignItems: "center" }}>
                        <HotelIcon sx={{ fontSize: "1rem", mr: 0.5, color: "text.secondary", flexShrink: 0 }} />
                        {it.bookings?.accommodation?.name}
                      </Typography>}
                      <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.95 }}>
                        {formatItineraryDates(it.dates)}
                        {/* Was `it.status ? 'Pending' : 'Confirmed'`, gated on
                            `typeoftravelers` — neither field means what that
                            implied. See utils/itineraryStatus. */}
                        <Chip
                          sx={{ fontSize: '12px', padding: 0, height: '16px', marginTop: '1.5px' }}
                          label={itineraryStatus(it).label}
                          size="small"
                          variant="text"
                          color={itineraryStatus(it).color}
                        />
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {it.vacationtype && <Chip label={it.vacationtype} size="small" color="primary" variant="outlined" />}
                        {it.typeoftravelers && <Chip label={it.typeoftravelers} size="small" variant="outlined" />}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", color: "primary.main", flexShrink: 0 }}>
                      <Typography variant="button">View</Typography>
                      <ChevronRightRoundedIcon fontSize="small" />
                    </Box>
                  </Box>
                </ItineraryCard>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.primary" sx={{ mb: 0.5 }}>
                No itineraries found.
              </Typography>
              <Typography color="text.secondary">
                Your saved itineraries will appear here once you create one.
              </Typography>
            </Box>
          )}
        </Box>
      </Main>
    </PageTemplate>
  );
}
